import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Return, type Sender } from '@accounts/domain';
import { usePresence } from '@accounts/session';
import { Refusal } from '@accounts/transport';
import { Button, Callout, Loading } from '@beyond-js/ui/react';
import { Wordmark } from '@accounts/ui';

/**
 * Signing out of Beyond, asked by a product: `/leave?product=<id>&return=<address>`. Every web product's
 * "Sign out of Beyond" lands here after ending its own session (Q09), and so does Accounts' own bar.
 *
 * It ends the Beyond session held here, which ends every product session derived from it, then says so
 * and offers the way back to the product that asked only when the service validated that address. The
 * address is validated whether or not anyone is still signed in here: a product may send a person whose
 * Beyond session already ended. It
 * says so only when the service confirmed it, or when nobody was signed in here any more: an authority
 * that refused ended nothing, and the person is told and can try again. When the request got no answer
 * the session may or may not have ended, so this asks who is here: nobody means it ended, and a service
 * that still does not answer is said to be unknown rather than "every session is still open".
 */
export function Leave() {
	const { text, accounts, arrive, settle } = usePresence();
	const [parameters] = useSearchParams();
	const [sender, setSender] = useState<Sender>();
	const [state, setState] = useState<'working' | 'done' | 'failed' | 'unknown'>('working');
	const started = useRef(false);

	const leave = useCallback(async () => {
		setState('working');
		const outcome = await accounts.leave().then(
			() => 'done' as const,
			async error => {
				if (Refusal.is(error) && error.unauthenticated) return 'done' as const;
				if (!Refusal.is(error) || !error.unknown) return 'failed' as const;
				return accounts.identity({ ended: 'caller' }).then(
					() => 'failed' as const,
					gone => (Refusal.is(gone) && gone.unauthenticated ? ('done' as const) : ('unknown' as const))
				);
			}
		);
		setState(outcome);
		await settle();
	}, [accounts, settle]);

	useEffect(() => {
		if (started.current) return;
		started.current = true;
		void (async () => {
			setSender(await arrive(parameters));
			await leave();
		})();
	}, [arrive, leave, parameters]);

	return (
		<div className="mx-auto flex max-w-md flex-col gap-4 px-4 py-16">
			<Wordmark label="Accounts" />
			{state === 'working' ? <Loading label={text('leave.working')} /> : null}
			{state === 'done' ? <Callout tone="success" live title={text('leave.done')} /> : null}
			{state === 'failed' ? <Callout tone="warning" live title={text('leave.failed')} /> : null}
			{state === 'unknown' ? <Callout tone="warning" live title={text('state.unknown')} /> : null}
			{state === 'failed' || state === 'unknown' ? (
				<Button type="button" variant="primary" onClick={() => void leave()}>
					{text('leave.retry')}
				</Button>
			) : null}
			{state === 'done' && sender ? <a href={sender.destination}>{text('return.action', { product: sender.name })}</a> : null}
			{state === 'done' && !sender && Return.requested(parameters) && parameters.get(Return.PRODUCT) !== Return.OWN ? <p className="text-body text-ink-muted">{text('leave.ignored')}</p> : null}
			{/* Signing in again goes back to the product and place that were left, as the service validated them (ARR-17) */}
			{state === 'done' ? <Link to={sender ? `/signin?product=${encodeURIComponent(sender.product)}&return=${encodeURIComponent(sender.destination)}` : '/signin'}>{text('leave.signin')}</Link> : null}
		</div>
	);
}
